import { Badge } from '@/components/Badge';
import { Checkbox } from '@/components/Checkbox';
import { DropdownMenu } from '@/components/Dropdown/DropdownMenu';
import { DropdownMenuItem } from '@/components/Dropdown/DropdownMenuItem';
import { Contact } from '@/types/contact';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/formatDateTime';
import { motion } from 'framer-motion';
import { t } from 'i18next';
import { FaWpforms } from 'react-icons/fa';
import { FaEllipsisVertical } from 'react-icons/fa6';
import { IoMdCall, IoMdMail, IoMdPersonAdd } from 'react-icons/io';
import { MdDelete, MdEdit, MdFileDownload } from 'react-icons/md';
import { TbArrowsExchange } from 'react-icons/tb';

interface ContactCardProps {
  contact: Contact;
  onDelete: (contact: Contact) => void;
  onEdit: (contact: Contact) => void;
  onExport: (contact: Contact) => void;
  onSelect: (contact: Contact) => void;
  isSelected: boolean;
}

export const ContactCard = ({
  contact,
  onDelete,
  onEdit,
  onExport,
  onSelect,
  isSelected,
}: ContactCardProps) => {
  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .filter(name => name.length > 0)
      .slice(0, 2)
      .map(name => name.charAt(0).toUpperCase())
      .join('');
  };

  const getSourceIcon = (source: string | undefined) => {
    switch (source) {
      case 'lead_capture':
        return (
          <FaWpforms className="size-4" title={t('common:lead_capture')} />
        );
      case 'exchange':
        return (
          <TbArrowsExchange className="size-4" title={t('common:exchange')} />
        );
      default:
        return (
          <IoMdPersonAdd className="size-4" title={t('common:add_contact')} />
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'relative flex h-48 flex-col overflow-hidden rounded-2xl border-2 bg-white shadow-sm transition-all duration-200',
        isSelected
          ? 'border-linko-light shadow-lg shadow-linko-light/20'
          : 'border-gray-100 hover:border-gray-200 hover:shadow-md',
      )}
    >
      {/* Header avec checkbox et menu */}
      <div className="flex items-center justify-between p-3 pb-0">
        <div onClick={e => e.stopPropagation()}>
          <Checkbox
            id={`select-${contact.id}`}
            checked={isSelected}
            onChange={() => onSelect(contact)}
            className="scale-110"
          />
        </div>
        <div onClick={e => e.stopPropagation()}>
          <DropdownMenu
            trigger={
              <motion.div
                whileTap={{ scale: 0.9 }}
                className="rounded-full p-2 hover:bg-gray-100"
              >
                <FaEllipsisVertical className="size-4 text-gray-500" />
              </motion.div>
            }
          >
            <DropdownMenuItem
              onClick={() => onEdit(contact)}
              className="flex w-full items-center gap-3 text-left"
            >
              <MdEdit className="size-5 text-linko-light" />
              {t('common:edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onExport(contact)}
              className="flex w-full items-center gap-3 text-left"
            >
              <MdFileDownload className="size-5 text-green-500" />
              {t('common:export')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(contact)}
              className="flex w-full items-center gap-3 text-left text-red-500"
            >
              <MdDelete className="size-5" />
              {t('common:delete')}
            </DropdownMenuItem>
          </DropdownMenu>
        </div>
      </div>

      {/* Contenu principal cliquable */}
      <div
        className="flex flex-1 cursor-pointer flex-col p-3 pt-1"
        onClick={() => onEdit(contact)}
      >
        {/* Avatar et nom */}
        <div className="flex items-start gap-3">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="relative shrink-0"
          >
            <div className="flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-linko-dark to-linko-light text-sm font-bold text-white shadow-lg">
              {getInitials(contact.full_name)}
            </div>
          </motion.div>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-bold text-gray-900">
              {contact.full_name}
            </h3>

            {/* Informations de contact */}
            <div className="mt-1 space-y-1">
              {contact.email && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <IoMdMail className="size-3 shrink-0 text-linko-light" />
                  <span className="truncate">{contact.email}</span>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <IoMdCall className="size-3 shrink-0 text-green-500" />
                  <span className="truncate">{contact.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tags */}
        {contact.tags && contact.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {contact.tags.slice(0, 2).map(tag => (
              <Badge
                key={tag.id}
                label={tag.name}
                color="gray"
                className="truncate text-xs"
              />
            ))}
            {contact.tags.length > 2 && (
              <Badge
                label={`+${contact.tags.length - 2}`}
                color="gray"
                className="text-xs"
              />
            )}
          </div>
        )}

        {/* Footer avec date et source */}
        <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-2">
          <span className="truncate text-xs text-gray-500">
            {formatDateTime(contact.created_at)}
          </span>

          <div className="flex items-center gap-2">
            <Badge
              icon={getSourceIcon(contact.source)}
              color={contact.source ? 'blue' : 'gray'}
              className="text-xs"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
